import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios, { API_BASE, formatApiErrorDetail, onSessionExpired } from "@/lib/api";

// user states:
//   null      → still checking (loading)
//   undefined → confirmed signed out
//   object    → signed in
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // true when a signed-in coach's sign-in ran out and could not be
  // renewed (NP-2). Cleared by signing in again or signing out.
  const [sessionExpired, setSessionExpired] = useState(false);
  const userRef = useRef(user);
  userRef.current = user;

  // A request failed with 401 and /auth/refresh could not renew the
  // sign-in (see lib/api.js). Only a coach we believed was signed in is
  // told their sign-in expired; startup checks for visitors stay silent.
  useEffect(
    () =>
      onSessionExpired(() => {
        if (userRef.current && typeof userRef.current === "object") {
          setUser(undefined);
          setSessionExpired(true);
        }
      }),
    []
  );

  // GET /auth/me goes through the same 401 → refresh → retry logic, so an
  // expired 24-hour access cookie with a valid refresh cookie still
  // resolves to the signed-in coach here.
  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/auth/me`);
      setUser(data);
      setSessionExpired(false);
    } catch {
      setUser(undefined);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" &&
        window.location.hash?.includes("session_id=")) {
      // Keep user null so ProtectedRoute (if any) waits, but not indefinitely.
      // AuthCallback flow will call refresh() explicitly on success.
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_BASE}/auth/login`,
        { email, password });
      setUser(data);
      setSessionExpired(false);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API_BASE}/auth/register`,
        { email, password, name });
      setUser(data);
      setSessionExpired(false);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API_BASE}/auth/logout`); } catch { /* ignore */ }
    setUser(undefined);
    setSessionExpired(false);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
